import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function FreeFallCalculator() {
  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Physics", url: "https://calclab.app/physics" },
      { name: "Free Fall Calculator", url: "https://calclab.app/calculator/free-fall" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Free Fall Calculator",
      "Calculate distance fallen under gravity using d = ½gt². Free online physics calculator.",
      "https://calclab.app/calculator/free-fall",
      lang
    )
  ]);
  const [time, setTime] = useState("");
  const [gravity, setGravity] = useState("9.81");
  const [result, setResult] = useState<number | null>(null);

  const calculateDistance = () => {
    const t = parseFloat(time);
    const g = parseFloat(gravity);

    if (isNaN(t) || isNaN(g)) {
      toast.error("Please enter valid values");
      return;
    }

    const distance = 0.5 * g * t * t;
    setResult(distance);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setTime("");
    setGravity("9.81");
    setResult(null);
  };

  return (
    <PageLayout calculatorName="Free Fall Calculator" calculatorPath="/calculator/free-fall">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Free Fall Calculator</h1>
          <p className="text-muted-foreground">
            Calculate distance fallen under gravity
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Formula: d = ½gt²</CardTitle>
            <CardDescription>
              Where d is distance (m), g is gravity (m/s²), and t is time (s)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="time">Time (s)</Label>
                <Input
                  id="time"
                  type="number"
                  step="0.01"
                  placeholder="Enter time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="gravity">Gravity (m/s²)</Label>
                <Input
                  id="gravity"
                  type="number"
                  step="0.01"
                  placeholder="Enter gravity"
                  value={gravity}
                  onChange={(e) => setGravity(e.target.value)}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={calculateDistance} className="flex-1">
                <Calculator className="mr-2 h-4 w-4" />
                Calculate
              </Button>
              <Button onClick={reset} variant="outline">
                Reset
              </Button>
            </div>

            {result !== null && (
              <>
                <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                  <CardHeader>
                    <CardTitle className="text-xl">Result</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-primary">
                      {result.toFixed(2)} m
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Distance Fallen
                    </p>
                  </CardContent>
                </Card>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["free-fall"]} trigger={result} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">How to use</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>1. Enter the time of fall in seconds</p>
                <p>2. Use default gravity (9.81 m/s²) or enter custom value</p>
                <p>3. Click Calculate to get distance fallen</p>
                <p className="pt-2 font-medium text-foreground">
                  Example: Free fall for 3 s = 44.145 m distance
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
